import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboard, getCustomers, getSites, getHeatPumps } from "../lib/sync";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Plus,
  Copy,
  FileCheck2,
  Search,
  CalendarClock,
  Users,
  Thermometer,
  ClipboardCheck,
  ChevronRight,
  MapPin,
  UsersRound,
  TrendingUp,
  Wrench,
} from "lucide-react";

const statusColor = (s) =>
  s === "completed"
    ? "bg-primary/10 text-primary"
    : "bg-amber-100 text-amber-700";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [q, setQ] = useState("");
  const [pumps, setPumps] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    getDashboard().then(setData).catch(() => {});
    getCustomers().then(setCustomers).catch(() => {});
    getSites().then(setSites).catch(() => {});
    getHeatPumps().then(setPumps).catch(() => {});
    if (isAdmin) api.get("/admin/overview").then(({ data }) => setOverview(data)).catch(() => {});
  }, [isAdmin]);

  const results = useMemo(() => {
    if (!q.trim()) return null;
    const t = q.toLowerCase();
    const custMap = Object.fromEntries(customers.map((c) => [c.id, c]));
    const out = [];
    customers
      .filter((c) =>
        [c.name, c.contact_person, c.email].join(" ").toLowerCase().includes(t)
      )
      .forEach((c) => out.push({ type: "Customer", label: c.name, sub: c.contact_person, onClick: () => navigate(`/customers/${c.id}`) }));
    sites
      .filter((s) => [s.name, s.address].join(" ").toLowerCase().includes(t))
      .forEach((s) =>
        out.push({ type: "Site", label: s.name, sub: s.address, onClick: () => navigate(`/customers/${s.customer_id}`) })
      );
    pumps
      .filter((p) =>
        [p.manufacturer, p.model, p.serial_number].join(" ").toLowerCase().includes(t)
      )
      .forEach((p) =>
        out.push({
          type: "Heat pump",
          label: `${p.manufacturer} ${p.model}`,
          sub: `S/N ${p.serial_number} • ${custMap[p.customer_id]?.name || ""}`,
          onClick: () => navigate(`/heatpump/${p.id}`),
        })
      );
    return out.slice(0, 12);
  }, [q, customers, sites, pumps, navigate]);

  const counts = data?.counts || {};

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-slate-900">
          Hi {user?.name?.split(" ")[0] || "there"} 👋
        </h1>
        <p className="text-sm text-slate-500">
          {isAdmin
            ? "Team overview and everything happening across Sterio."
            : `Here's what's on for ${new Date().toLocaleDateString("en-IE", { weekday: "long", day: "numeric", month: "long" })}.`}
        </p>
      </div>

      {isAdmin && overview && (
        <section className="space-y-3" data-testid="admin-overview">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat Icon={UsersRound} value={`${overview.active_engineers}/${overview.total_engineers}`} label="Active eng." />
            <Stat Icon={TrendingUp} value={overview.reports_this_month} label="Reports / month" />
            <Stat Icon={ClipboardCheck} value={overview.completed_total} label="Completed" />
            <Stat Icon={CalendarClock} value={overview.scheduled_jobs} label="Scheduled" />
          </div>
          <Card className="p-4">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="font-heading text-sm font-bold text-slate-900">Engineer activity</h2>
              <button onClick={() => navigate("/team")} className="text-xs font-semibold text-primary" data-testid="manage-team-link">
                Manage team
              </button>
            </div>
            <div className="space-y-1.5" data-testid="engineer-activity">
              {overview.per_engineer.map((e) => (
                <div key={e.id} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-sm last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800">{e.name}</span>
                    {!e.active && <Badge className="bg-red-100 text-red-700 text-[10px]" variant="secondary">Inactive</Badge>}
                  </div>
                  <span className="text-xs text-slate-500">
                    {e.completed} completed{e.drafts ? ` • ${e.drafts} drafts` : ""}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </section>
      )}

      {/* search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search customers, sites or heat pump units…"
          className="h-12 pl-10"
          data-testid="global-search"
        />
        {results && (
          <Card className="absolute z-20 mt-2 max-h-80 w-full overflow-auto p-1.5 shadow-lg">
            {results.length === 0 ? (
              <p className="px-3 py-4 text-sm text-slate-400">No matches.</p>
            ) : (
              results.map((r, i) => (
                <button
                  key={i}
                  onClick={r.onClick}
                  data-testid={`search-result-${i}`}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left hover:bg-slate-50"
                >
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{r.label}</div>
                    <div className="text-xs text-slate-400">{r.sub}</div>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{r.type}</Badge>
                </button>
              ))
            )}
          </Card>
        )}
      </div>

      {/* start a new report */}
      <Card className="p-4" data-testid="new-report-section">
        <h2 className="mb-3 font-heading text-sm font-bold text-slate-900">Start a new report</h2>
        <div className="grid grid-cols-2 gap-2">
          <Button className="h-12" onClick={() => navigate("/report/new?type=service_report")} data-testid="new-service-report">
            <Wrench className="mr-2 h-4 w-4" /> Service
          </Button>
          <Button variant="outline" className="h-12" onClick={() => navigate("/report/new?type=commissioning_report")} data-testid="new-commissioning-report">
            <ClipboardCheck className="mr-2 h-4 w-4" /> Commissioning
          </Button>
        </div>
      </Card>

      {/* quick actions */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <ActionTile testid="action-new-report" Icon={Plus} label="New report" onClick={() => navigate("/report/choose")} primary />
        {isAdmin ? (
          <>
            <ActionTile testid="action-team" Icon={UsersRound} label="Team" onClick={() => navigate("/team")} />
            <ActionTile testid="action-schedule" Icon={CalendarClock} label="Schedule" onClick={() => navigate("/schedule")} />
            <ActionTile testid="action-completed" Icon={FileCheck2} label="All reports" onClick={() => navigate("/reports")} />
          </>
        ) : (
          <>
            <ActionTile testid="action-duplicate" Icon={Copy} label="Duplicate" onClick={() => navigate("/reports?duplicate=1")} />
            <ActionTile testid="action-completed" Icon={FileCheck2} label="Completed" onClick={() => navigate("/reports?status=completed")} />
            <ActionTile testid="action-customers" Icon={Users} label="Customers" onClick={() => navigate("/customers")} />
          </>
        )}
      </div>

      {/* stats */}
      <div className="grid grid-cols-3 gap-3">
        <Stat Icon={Users} value={counts.customers ?? "—"} label="Customers" />
        <Stat Icon={Thermometer} value={counts.heatpumps ?? "—"} label="Heat pumps" />
        <Stat Icon={ClipboardCheck} value={counts.completed_reports ?? "—"} label="Completed" />
      </div>

      {/* upcoming jobs */}
      <section>
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
          <CalendarClock className="h-5 w-5 text-primary" /> Upcoming jobs
        </h2>
        <div className="space-y-2.5" data-testid="upcoming-jobs">
          {(data?.upcoming_jobs || []).length === 0 && (
            <p className="text-sm text-slate-400">No scheduled jobs.</p>
          )}
          {(data?.upcoming_jobs || []).map((j) => (
            <Card
              key={j.id}
              className="flex items-center justify-between p-4"
              data-testid={`job-${j.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-900">{j.customer_name}</span>
                  <Badge variant="outline" className="text-[10px]">{j.job_type}</Badge>
                </div>
                <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
                  <MapPin className="h-3 w-3" /> {j.site_address || j.site_name}
                </div>
              </div>
              <div className="flex items-center gap-3 pl-3">
                <div className="text-right">
                  <div className="text-xs font-semibold text-primary">
                    {new Date(j.scheduled_date).toLocaleDateString("en-IE", { day: "numeric", month: "short" })}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() =>
                    navigate(`/report/new?customer=${j.customer_id}&site=${j.site_id}&pump=${j.heat_pump_id}`)
                  }
                  data-testid={`job-start-${j.id}`}
                >
                  Start
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* recent reports */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
            <FileCheck2 className="h-5 w-5 text-primary" /> Recent reports
          </h2>
          <button onClick={() => navigate("/reports")} className="text-sm font-semibold text-primary" data-testid="view-all-reports">
            View all
          </button>
        </div>
        <div className="space-y-2.5" data-testid="recent-reports">
          {(data?.recent_reports || []).slice(0, 5).map((r) => (
            <Card
              key={r.id}
              onClick={() => navigate(`/report/${r.id}/view`)}
              className="flex cursor-pointer items-center justify-between p-4 hover:border-primary/40"
              data-testid={`report-${r.id}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-bold text-slate-900">{r.customer_name}</div>
                <div className="text-xs text-slate-500">
                  {r.service_date} • {r.engineer_name}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${statusColor(r.status)}`}>
                  {r.status}
                </span>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </div>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}

function ActionTile({ Icon, label, onClick, primary, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex h-24 flex-col items-center justify-center gap-2 rounded-xl border text-sm font-semibold transition-transform active:scale-[0.97] ${
        primary
          ? "border-primary bg-primary text-white"
          : "border-border bg-white text-slate-700 hover:border-primary/40"
      }`}
    >
      <Icon className="h-6 w-6" />
      {label}
    </button>
  );
}

function Stat({ Icon, value, label }) {
  return (
    <Card className="flex flex-col items-center justify-center p-4">
      <Icon className="h-5 w-5 text-primary" />
      <div className="mt-1 font-heading text-2xl font-extrabold text-slate-900">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    </Card>
  );
}
